import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import {
  trackPricesSelected,
  trackProductsSelected,
} from "@/features/analytics/trackers";
import { trackPlanPageViewed } from "@/features/analytics/trackers/plan-page-viewed";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import type { Product } from "@/features/products/models";
import { LogoLoader } from "@/shared/components";
import type { Price } from "@/shared/domains";
import { CATEGORY_SLUG, PRODUCT_SLUG } from "@/shared/domains";
import { errorToast } from "@/shared/ui";
import { formatPrice } from "@/shared/utils";
import {
  FunnelAccordion,
  FunnelPaymentIcons,
  FunnelTimerBanner,
  FunnelTrustpilot,
  useCountdown,
} from "../components";
import { FUNNEL_ROUTE } from "../router/constants";

const RESERVED_SECONDS = 596;

interface PlanMeta {
  title: string;
  badge: string;
  badgeColor: string;
  supply: string;
  description: string;
}

const getMonthlyMeta = (): PlanMeta => ({
  title: t`Monthly Plan`,
  badge: t`Lowest Starting Price`,
  badgeColor: "bg-primary-950",
  supply: t`4 Week Supply`,
  description: t`The new you, delivered to your door monthly`,
});

const getPlanMeta = (): Record<number, PlanMeta> => ({
  3: {
    title: t`3-Month Plan`,
    badge: t`Great Value`,
    badgeColor: "bg-secondary-500",
    supply: t`12 Week Supply`,
    description: t`Receive your 3 month supply in a single shipment`,
  },
  6: {
    title: t`6-Month Plan`,
    badge: t`Most Popular`,
    badgeColor: "bg-secondary-500",
    supply: t`24 Week Supply`,
    description: t`Your ultimate plan to guaranteed success and your consistency`,
  },
  12: {
    title: t`12-Month Plan`,
    badge: t`Biggest Savings`,
    badgeColor: "bg-secondary-500",
    supply: t`48 Week Supply`,
    description: t`Commit to a year of progress and save on your new self`,
  },
  1: getMonthlyMeta(),
});

const getIncludedFeatures = () => [
  t`Care Coaching`,
  t`Bundle & save options`,
  t`Unlimited free doctor consultations`,
  t`Free expedited shipping*`,
  t`Home injection kit`,
  t`24/7 customer support`,
  t`Free dietitian consultation`,
  t`Complimentary menu options, built to fit your plan`,
];

const effectivePrice = (price: Price) =>
  price.discount?.isValid ? price.discount.priceAfterDiscount : price.price;

// Social-proof caption per treatment. Products not listed here (e.g. oral
// semaglutide) render no caption line at all.
const SOCIAL_PROOF: Record<string, string> = {
  [PRODUCT_SLUG.SEMAGLUTIDE_INJECTABLE]: t`Best Value`,
  [PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE]: t`Our most-selected option`,
};

// Dedicated transparent-background renders for this card, kept separate from
// product.thumbnail since that field is shared with other screens that rely
// on its background-filled image (e.g. object-cover hero images).
const TREATMENT_CARD_IMAGE: Record<string, string> = {
  [PRODUCT_SLUG.SEMAGLUTIDE_INJECTABLE]:
    "/products/alternative-funnel/semaglutide-vial.webp",
  [PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE]:
    "/products/alternative-funnel/tirzepatide-vial.webp",
  [PRODUCT_SLUG.SEMAGLUTIDE_ORAL]:
    "/products/alternative-funnel/oral-semaglutide-vial.webp",
};

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5 shrink-0 text-secondary-400"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path
      d="m8 12 3 3 5-6"
      fill="none"
      stroke="white"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TreatmentCard = ({
  product,
  selected,
  recommended,
  onSelect,
}: {
  product: Product;
  selected: boolean;
  recommended: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`relative flex w-full items-center gap-4 overflow-hidden rounded-2xl border-2 bg-white px-5 py-5 text-left transition ${
      selected ? "border-secondary-500 bg-secondary-50" : "border-secondary-200"
    }`}
  >
    {recommended && (
      <span className="absolute top-0 left-0 rounded-br-lg bg-secondary-500 px-3 py-1 text-xs font-bold text-white">
        {t`Recommended`}
      </span>
    )}
    <img
      src={TREATMENT_CARD_IMAGE[product.slug] ?? product.thumbnail ?? "#"}
      alt={product.name}
      className="size-28 shrink-0 rounded-lg bg-secondary-100 object-contain p-1"
    />
    <div className="grow">
      <h3 className="text-lg font-bold text-neutral-900">{product.name}</h3>
      <p className="mt-1 text-sm text-neutral-600">
        {recommended
          ? t`Faster results, dual-action, but more expensive.`
          : t`Proven, effective, more affordable.`}
      </p>
      {product.slug !== PRODUCT_SLUG.SEMAGLUTIDE_ORAL && (
        <span
          className={`mt-2 inline-block rounded px-2 py-0.5 text-xs font-semibold ${
            recommended
              ? "bg-secondary-100 text-primary-950"
              : "bg-green-100 text-green-700"
          }`}
        >
          {recommended ? t`Fastest Results` : t`More Affordable`}
        </span>
      )}
      {SOCIAL_PROOF[product.slug] && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-neutral-600">
          <span className="size-2 rounded-full bg-green-500" />
          {SOCIAL_PROOF[product.slug]}
        </p>
      )}
    </div>
    <span
      className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 ${
        selected
          ? "border-secondary-500 bg-secondary-500"
          : "border-secondary-200"
      }`}
    >
      {selected && (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth={3}
          className="size-3.5"
          aria-hidden="true"
        >
          <path
            d="M20 6 9 17l-5-5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  </button>
);

const PlanCard = ({
  price,
  referenceMonthly,
  onSelect,
}: {
  price: Price;
  referenceMonthly: number;
  onSelect: () => void;
}) => {
  const meta = getPlanMeta()[price.durationInMonths] ?? getMonthlyMeta();
  const total = effectivePrice(price);
  const monthly = total / price.durationInMonths;
  const savings = Math.round(
    (referenceMonthly - monthly) * price.durationInMonths,
  );
  // Plain variables so they can be interpolated in ttag strings.
  const durationInMonths = price.durationInMonths;
  const formattedSavings = formatPrice(savings);

  return (
    <div className="rounded-2xl border border-secondary-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div>
          <span className="flex items-center gap-2 text-xl font-extrabold text-neutral-900">
            {meta.title}
            <span
              className={`rounded-full px-3 py-0.5 text-xs font-bold text-white ${meta.badgeColor}`}
            >
              {meta.badge}
            </span>
          </span>
          <p className="mt-1 text-sm text-neutral-600">{meta.description}</p>
        </div>
        <span className="text-sm font-semibold whitespace-nowrap text-neutral-700">
          {meta.supply}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-neutral-900">{t`Easy 0% Installments`}</p>
          <p className="text-sm text-neutral-600">{t`Spread payments over 12 months`}</p>
        </div>
      </div>

      {savings > 0 && (
        <div className="mt-4 rounded-lg bg-secondary-500 py-3 text-center text-base font-bold text-white">
          {durationInMonths > 1
            ? t`Save ${formattedSavings} with ${durationInMonths} months supply`
            : t`You are saving ${formattedSavings}`}
        </div>
      )}

      <button
        type="button"
        onClick={onSelect}
        className="mt-3 w-full rounded-lg bg-primary-950 py-4 text-center text-base font-bold text-white transition hover:bg-primary-900 active:bg-primary-800"
      >
        {t`Select Plan`} | {formatPrice(monthly)} {t`/ month`}
      </button>
    </div>
  );
};

export const Pricing = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();
  const time = useCountdown(RESERVED_SECONDS);

  const user = useOnboardingUser();
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const setSelectedCategories = useOnboardingStore(
    (state) => state.setSelectedCategories,
  );
  const addSelectedProduct = useOnboardingStore(
    (state) => state.addSelectedProduct,
  );
  const setSelectedPrice = useOnboardingStore(
    (state) => state.setSelectedPrice,
  );
  const priceVariant = useOnboardingStore(
    (state) => state.urlParams.price_variant,
  );
  const couponCode = useOnboardingStore((state) => state.couponCode);
  const urlParamsResolved = useOnboardingStore(
    (state) => state.urlParamsResolved,
  );

  const {
    data: productGroups,
    isLoading,
    isError,
  } = useQuery({
    ...productsQueriesStore.products.category._ctx.products({
      categorySlug: CATEGORY_SLUG.WEIGHT_LOSS,
      priceVariant,
      couponCode,
    }),
    // Wait for urlParamsResolved so this never fires with priceVariant still
    // undefined-because-not-yet-read (as opposed to genuinely absent) - see
    // DEV-73391. Without this, a reload landing directly on this screen can
    // fetch and auto-select the default-priced product before the correctly
    // scoped response arrives, and that stale selection is never corrected.
    enabled: urlParamsResolved,
  });

  useEffect(() => {
    if (isError) {
      errorToast(
        t`We couldn't load the available treatments. Please try again.`,
      );
    }
  }, [isError]);

  const treatments = (productGroups ?? [])
    .flatMap((group) => group.products)
    .filter(
      (product, index, all) =>
        all.findIndex((current) => current.id === product.id) === index,
    )
    // A product with no prices for the resolved variant is a dead end if
    // selected (Step 2 would render no plans), so it's excluded from Step 1
    // rather than left selectable.
    .filter((product) => product.prices.length > 0);

  useEffect(() => {
    if (!productGroups) {
      return;
    }

    // Only auto-select from `treatments`, not the raw product list — a
    // variant can return tirzepatide with no prices, and selecting it would
    // leave Step 1 with no highlighted card and Step 2 with no plans.
    const defaultProduct =
      treatments.find(
        (product) => product.slug === PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE,
      ) ?? treatments[0];

    const category = defaultProduct?.category;
    if (!category) {
      return;
    }

    const existing = useOnboardingStore.getState().selectedCategories;
    if (!existing.some((current) => current.id === category.id)) {
      setSelectedCategories([{ ...category, isNew: false }]);
    }
    if (
      !useOnboardingStore
        .getState()
        .selectedCategories.some(
          (current) => current.selectedProduct?.id === defaultProduct.id,
        )
    ) {
      addSelectedProduct(defaultProduct);
    }
  }, [productGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      return;
    }
    trackPlanPageViewed({
      customerIoAnalytics: analytics,
      user,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  if (!user) {
    return <Navigate to={FUNNEL_ROUTE.CONTACT} replace />;
  }

  const selectedProduct = selectedCategories[0]?.selectedProduct;

  // Savings are always measured against the 1-month plan's undiscounted base
  // price (e.g. $349/mo), not the discounted prices — otherwise a discount on
  // the monthly plan skews the reference and every plan's "save" amount.
  const monthlyPlan = selectedProduct?.prices.find(
    (price) => price.durationInMonths === 1,
  );
  const referenceMonthly = monthlyPlan
    ? monthlyPlan.price / monthlyPlan.durationInMonths
    : selectedProduct
      ? Math.max(
          ...selectedProduct.prices.map(
            (price) => price.price / price.durationInMonths,
          ),
        )
      : 0;

  const handleSelectTreatment = (product: Product) => {
    addSelectedProduct(product);
    trackProductsSelected({
      selectedCategories: useOnboardingStore.getState().selectedCategories,
      userId: user.email,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
  };

  const handleSelectPlan = (product: Product, price: Price) => {
    addSelectedProduct(product);
    setSelectedPrice(product.category.id, price);
    trackPricesSelected({
      selectedCategories: useOnboardingStore.getState().selectedCategories,
      userId: user.email,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
    navigate(FUNNEL_ROUTE.PAYMENT);
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-5 py-6">
      <LogoLoader show={!urlParamsResolved || isLoading} fullscreen />

      <FunnelTimerBanner time={time} />

      <h1 className="mt-8 text-center text-3xl font-extrabold text-neutral-900 sm:text-4xl">
        {t`Clear Costs. Complete Care.`}
      </h1>
      <p className="mt-2 text-center text-base font-semibold text-neutral-700">
        {t`Every dosage level included — without extra fees.`}
      </p>

      <div className="mt-8 flex items-center gap-3">
        <span className="rounded-full bg-primary-950 px-4 py-1 text-sm font-bold text-white">
          {t`Step 1`}
        </span>
        <h2 className="text-xl font-bold text-neutral-900">{t`Select Treatment`}</h2>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        {treatments.map((product) => (
          <TreatmentCard
            key={product.id}
            product={product}
            selected={selectedProduct?.id === product.id}
            recommended={product.slug === PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE}
            onSelect={() => handleSelectTreatment(product)}
          />
        ))}
      </div>

      <div className="mt-10 flex items-center gap-3">
        <span className="rounded-full bg-primary-950 px-4 py-1 text-sm font-bold text-white">
          {t`Step 2`}
        </span>
        <h2 className="text-xl font-bold text-neutral-900">{t`Select Your Plan`}</h2>
      </div>
      <p className="mt-2 text-neutral-700">
        {t`Lock in your savings without a big upfront payment — use free financing or pay in full with your card.`}
      </p>

      <div className="mt-6 flex flex-col gap-5">
        {selectedProduct?.prices.map((price) => (
          <PlanCard
            key={price.id}
            price={price}
            referenceMonthly={referenceMonthly}
            onSelect={() => handleSelectPlan(selectedProduct, price)}
          />
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-4">
        <FunnelAccordion title={t`All Plans Include`}>
          <ul className="flex flex-col gap-3">
            {getIncludedFeatures().map((feature) => (
              <li
                key={feature}
                className="flex items-center gap-3 text-neutral-800"
              >
                <CheckIcon />
                {feature}
              </li>
            ))}
          </ul>
        </FunnelAccordion>

        <FunnelAccordion title={t`How It Works`}>
          <p className="text-neutral-700">
            {t`Each month includes one shot per week, for a total of four shots. Your provider will start you on a low dose and gradually increase it to your ideal level, helping you lose weight safely and effectively.`}
          </p>
        </FunnelAccordion>

        <div className="rounded-2xl bg-white/60 p-5">
          <p className="text-sm font-bold text-neutral-900">
            {t`All major credit cards accepted`}
          </p>
          <div className="mt-3">
            <FunnelPaymentIcons />
          </div>
          <p className="mt-4 text-base font-semibold text-neutral-800">
            <span className="text-green-600">✓</span> {t`HSA/FSA Eligible`}
          </p>
          <p className="mt-4 text-sm font-bold text-neutral-900">{t`Buy Now, Pay Later`}</p>
          <div className="mt-2 flex gap-2">
            <span className="rounded bg-[#ffd6e7] px-3 py-1 text-sm font-bold text-neutral-900">
              Klarna.
            </span>
          </div>
        </div>

        <div className="py-4">
          <FunnelTrustpilot />
        </div>
      </div>
    </div>
  );
};
